import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText, InspectorControls, InnerBlocks } from '@wordpress/block-editor';
import { 
    PanelBody, 
    TabPanel
} from '@wordpress/components';

// Import custom components
import Typography from '../../components/Typography';
import VisibilityControl from '../../components/VisibilityControl';
import Spacing from '../../components/Spacing';
import Border from '../../components/Border';
import FlatColorPicker from '../../components/FlatColorPicker';
import { getSpacingStyles, getBorderStyles, getTypographyStyles } from '../../utils/styles';

registerBlockType('kanews/faq-item', {
    apiVersion: 2,
    title: __('SSS Öğesi', 'kanews'),
    icon: 'editor-help',
    category: 'kanews',
    parent: ['kanews/faq'],
    attributes: {
        question: {
            type: 'string',
            default: __('Soru', 'kanews')
        },
        questionTypography: {
            type: 'object',
            default: {}
        },
        answerTypography: {
            type: 'object',
            default: {}
        },
        questionBackground: {
            type: 'string',
            default: ''
        },
        answerBackground: {
            type: 'string',
            default: ''
        },
        margin: {
            type: 'object',
            default: {}
        },
        padding: {
            type: 'object',
            default: {}
        },
        border: {
            type: 'object',
            default: {}
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        },
        accordionId: {
            type: 'string',
            default: ''
        },
        itemId: {
            type: 'string',
            default: ''
        }
    },
    edit: ({ attributes, setAttributes }) => {
        const {
            question,
            questionTypography,
            answerTypography,
            questionBackground,
            answerBackground,
            margin,
            padding,
            border,
            visibility,
            accordionId,
            itemId
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const blockProps = useBlockProps();

        const wrapperProps = {
            className: ['collapse-item', 'kanews-block-margin', 'kanews-block-border', 'kanews-block-padding', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin'),
                ...getSpacingStyles(padding, 'padding'),
                ...getBorderStyles(border, 'border')
            }
        };

        // Use existing itemId or generate a new one
        const currentItemId = itemId || `faq-item-${blockProps.id}`;
        if (!itemId) {
            setAttributes({ itemId: currentItemId });
        }

        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tab-panel"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <>
                                    {tab.name === 'content' && (
                                        <PanelBody>
                                            <RichText
                                                tagName="h3"
                                                value={question}
                                                onChange={(value) => setAttributes({ question: value })}
                                                placeholder={__('Soru', 'kanews')}
                                                className="collapse-item-header-title"
                                            />
                                        </PanelBody>
                                    )}
                                    {tab.name === 'design' && (
                                        <>
                                            <PanelBody title={__('Tipografi', 'kanews')}>
                                                <Typography
                                                    label={__('Soru', 'kanews')}
                                                    value={questionTypography}
                                                    onChange={(value) => setAttributes({ questionTypography: value })}
                                                />
                                                <Typography
                                                    label={__('Cevap', 'kanews')}
                                                    value={answerTypography}
                                                    onChange={(value) => setAttributes({ answerTypography: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Arkaplan', 'kanews')} initialOpen={false}>
                                                <FlatColorPicker
                                                    label={__('Soru Arkaplanı', 'kanews')}
                                                    value={questionBackground}
                                                    onChange={(value) => setAttributes({ questionBackground: value })}
                                                />
                                                <FlatColorPicker
                                                    label={__('Cevap Arkaplanı', 'kanews')}
                                                    value={answerBackground}
                                                    onChange={(value) => setAttributes({ answerBackground: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Kenarlık', 'kanews')} initialOpen={false}>
                                                <Border
                                                    label={__('Kenarlık', 'kanews')}
                                                    value={border}
                                                    onChange={(value) => setAttributes({ border: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Boşluklar', 'kanews')} initialOpen={false}>
                                                <Spacing
                                                    label={__('Dış Boşluk', 'kanews')}
                                                    value={margin}
                                                    onChange={(value) => setAttributes({ margin: value })}
                                                />
                                                <Spacing
                                                    label={__('İç Boşluk', 'kanews')}
                                                    value={padding}
                                                    onChange={(value) => setAttributes({ padding: value })}
                                                />
                                            </PanelBody>
                                        </>
                                    )}
                                    {tab.name === 'visibility' && (
                                        <PanelBody>
                                            <VisibilityControl
                                                value={visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                        </PanelBody>
                                    )}
                                </>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>

                <div {...blockProps}>
                    <div {...wrapperProps}>
                        <div 
                            className="collapse-item-header" 
                            id={`heading-${currentItemId}`}
                            style={{ backgroundColor: questionBackground }}
                        >
                            <button 
                                className="collapse-btn collapsed" 
                                type="button" 
                                data-toggle="collapse" 
                                data-target={`#collapse-${currentItemId}`} 
                                aria-expanded="false" 
                                aria-controls={`collapse-${currentItemId}`}
                            >
                                <RichText
                                    tagName="h3"
                                    value={question}
                                    onChange={(value) => setAttributes({ question: value })}
                                    placeholder={__('Soru', 'kanews')}
                                    className="collapse-item-header-title"
                                    style={getTypographyStyles(questionTypography)}
                                />
                            </button>
                        </div>
                        <div 
                            id={`collapse-${currentItemId}`} 
                            className="collapse" 
                            aria-labelledby={`heading-${currentItemId}`} 
                            data-parent={accordionId}
                        >
                            <div 
                                className="collapse-item-body"
                                style={{ backgroundColor: answerBackground }}
                            >
                                <div className="collapse-item-body-content" style={getTypographyStyles(answerTypography)}>
                                    <InnerBlocks
                                        allowedBlocks={['core/paragraph', 'core/heading', 'core/list', 'core/image', 'core/quote']}
                                        template={[
                                            ['core/paragraph', { placeholder: __('Cevap ekleyin...', 'kanews') }]
                                        ]}
                                        renderAppender={InnerBlocks.ButtonBlockAppender}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    },
    save: ({ attributes }) => {
        const {
            question,
            questionTypography,
            answerTypography,
            questionBackground,
            answerBackground,
            margin,
            padding,
            border,
            visibility,
            accordionId,
            itemId
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const wrapperProps = {
            className: ['collapse-item', 'kanews-block-margin', 'kanews-block-border', 'kanews-block-padding', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin'),
                ...getSpacingStyles(padding, 'padding'),
                ...getBorderStyles(border, 'border')
            }
        };

        return (
            <div {...wrapperProps}>
                <div 
                    className="collapse-item-header" 
                    id={`heading-${itemId}`}
                    style={{ backgroundColor: questionBackground }}
                >
                    <button 
                        className="collapse-btn collapsed" 
                        type="button" 
                        data-toggle="collapse" 
                        data-target={`#collapse-${itemId}`} 
                        aria-expanded="false" 
                        aria-controls={`collapse-${itemId}`}
                    >
                        <RichText.Content
                            tagName="h3"
                            value={question}
                            className="collapse-item-header-title"
                            style={getTypographyStyles(questionTypography)}
                        />
                    </button>
                </div>
                <div 
                    id={`collapse-${itemId}`} 
                    className="collapse" 
                    aria-labelledby={`heading-${itemId}`} 
                    data-parent={accordionId}
                >
                    <div 
                        className="collapse-item-body"
                        style={{ backgroundColor: answerBackground }}
                    >
                        <div className="collapse-item-body-content" style={getTypographyStyles(answerTypography)}>
                            <InnerBlocks.Content />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}); 